import { Component, ElementRef, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ConnectedPosition } from '@angular/cdk/overlay';

type Period = 'AM' | 'PM';
type Segment = 'hour' | 'minute';

const pad2 = (value: number) => String(value).padStart(2, '0');

/**
 * Campo de hora que se muestra en formato 12 horas (hh:mm AM/PM)
 * pero mantiene en el FormControl el valor en 24 horas 'HH:mm',
 * igual que un <input type="time"> nativo.
 */
@Component({
  selector: 'app-time-input-12h',
  templateUrl: './time-input-12h.component.html',
  styleUrls: ['./time-input-12h.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => TimeInput12hComponent),
    multi: true
  }]
})
export class TimeInput12hComponent implements ControlValueAccessor {
  @Input() readonly = false;
  /** Pinta el borde en rojo cuando no hay hora registrada. */
  @Input() highlightEmpty = false;
  /** Se emite después de actualizar el FormControl, solo cuando el usuario cambia la hora. */
  @Output() timeChange = new EventEmitter<string>();

  @ViewChild('hourEl') hourEl: ElementRef<HTMLInputElement>;
  @ViewChild('minuteEl') minuteEl: ElementRef<HTMLInputElement>;
  @ViewChild('panelEl') panelEl: ElementRef<HTMLElement>;

  hour = '';
  minute = '';
  /** null hasta que el usuario escriba A/P o lo elija; nunca se asume AM al digitar. */
  period: Period | null = null;
  disabled = false;

  // Selector desplegable (icono de reloj)
  pickerOpen = false;
  readonly hours = Array.from({ length: 12 }, (_, i) => pad2(i + 1));
  readonly minutes = Array.from({ length: 60 }, (_, i) => pad2(i));
  readonly periods: Period[] = ['AM', 'PM'];
  readonly pickerPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 }
  ];

  private lastValue = '';
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  get locked(): boolean {
    return this.readonly || this.disabled;
  }

  /** Hora y minutos escritos pero falta AM/PM: la hora todavía no se guarda. */
  get missingPeriod(): boolean {
    return this.hour.length === 2 && this.minute.length === 2 && !this.period;
  }

  togglePicker(): void {
    if (this.locked) { return; }
    this.pickerOpen = !this.pickerOpen;
  }

  closePicker(): void {
    if (!this.pickerOpen) { return; }
    this.pickerOpen = false;
    this.onTouched();
  }

  /** Al abrir, desplaza cada columna hasta el valor seleccionado. */
  onPickerAttach(): void {
    setTimeout(() => {
      const panel = this.panelEl?.nativeElement;
      if (!panel) { return; }
      panel.querySelectorAll<HTMLElement>('.t12-col').forEach(col => {
        const selected = col.querySelector<HTMLElement>('.t12-opt--selected');
        // .t12-col es position: relative, así offsetTop es relativo a la columna
        col.scrollTop = selected ? selected.offsetTop - 4 : 0;
      });
    });
  }

  pick(segment: 'hour' | 'minute' | 'period', value: string): void {
    if (this.locked) { return; }
    if (segment === 'hour') { this.hour = value; }
    if (segment === 'minute') { this.minute = value; }
    if (segment === 'period') { this.period = value as Period; }
    // Completa hora/minutos faltantes; AM/PM solo cuando el usuario lo elige
    if (!this.hour) { this.hour = '12'; }
    if (!this.minute) { this.minute = '00'; }
    this.emitIfChanged();
  }

  writeValue(value: string): void {
    const match = /^(\d{1,2}):(\d{2})/.exec(value || '');
    if (!match) {
      this.hour = '';
      this.minute = '';
      this.period = null;
      this.lastValue = '';
      return;
    }
    const h24 = Number(match[1]);
    this.period = h24 >= 12 ? 'PM' : 'AM';
    this.hour = this.pad(h24 % 12 === 0 ? 12 : h24 % 12);
    this.minute = match[2];
    this.lastValue = `${this.pad(h24)}:${match[2]}`;
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onSegmentInput(event: Event, segment: Segment): void {
    const input = event.target as HTMLInputElement;
    let digits = input.value.replace(/\D/g, '').slice(-2);

    if (segment === 'hour') {
      // Un primer dígito mayor a 1 solo puede ser una hora de un dígito (2..9)
      if (digits.length === 1 && Number(digits) > 1) { digits = '0' + digits; }
      if (digits.length === 2 && (Number(digits) < 1 || Number(digits) > 12)) { digits = digits.slice(-1); }
      this.hour = digits;
    } else {
      if (digits.length === 1 && Number(digits) > 5) { digits = '0' + digits; }
      if (digits.length === 2 && Number(digits) > 59) { digits = digits.slice(-1); }
      this.minute = digits;
    }
    input.value = digits;

    if (digits.length === 2 && segment === 'hour') {
      this.minuteEl?.nativeElement.focus();
    }
    this.emitIfChanged();
  }

  onSegmentKeydown(event: KeyboardEvent, segment: Segment): void {
    if (this.locked) { return; }
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.step(segment, event.key === 'ArrowUp' ? 1 : -1);
    } else if (event.key === 'a' || event.key === 'A' || event.key === 'p' || event.key === 'P') {
      event.preventDefault();
      this.setPeriod(event.key.toUpperCase() === 'A' ? 'AM' : 'PM');
    } else if (event.key === ':' && segment === 'hour') {
      event.preventDefault();
      this.minuteEl?.nativeElement.focus();
    } else if (event.key === 'Backspace' && segment === 'minute' && !this.minute) {
      this.hourEl?.nativeElement.focus();
    }
  }

  onSegmentBlur(segment: Segment): void {
    if (segment === 'hour' && this.hour.length === 1) { this.hour = this.pad(Number(this.hour) || 12); }
    if (segment === 'minute' && this.minute.length === 1) { this.minute = this.pad(Number(this.minute)); }
    this.emitIfChanged();
    this.onTouched();
  }

  togglePeriod(): void {
    if (this.locked) { return; }
    this.setPeriod(this.period === 'AM' ? 'PM' : 'AM');
  }

  /** Sobre el segmento AM/PM: A → AM, P → PM, flechas alternan, Backspace lo borra. */
  onPeriodKeydown(event: KeyboardEvent): void {
    if (this.locked) { return; }
    const key = event.key.toUpperCase();
    if (key === 'A' || key === 'P') {
      event.preventDefault();
      this.setPeriod(key === 'A' ? 'AM' : 'PM');
    } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      this.togglePeriod();
    } else if (event.key === 'Backspace' && this.period) {
      event.preventDefault();
      this.period = null;
    } else if (event.key === 'Backspace' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.minuteEl?.nativeElement.focus();
    }
  }

  selectAll(event: FocusEvent): void {
    (event.target as HTMLInputElement).select();
  }

  private setPeriod(period: Period): void {
    this.period = period;
    this.emitIfChanged();
  }

  private step(segment: Segment, delta: number): void {
    if (segment === 'hour') {
      const current = Number(this.hour) || (delta > 0 ? 0 : 1);
      const next = ((current - 1 + delta + 12) % 12) + 1;
      this.hour = this.pad(next);
    } else {
      const current = Number(this.minute) || 0;
      this.minute = this.pad((current + delta + 60) % 60);
    }
    this.emitIfChanged();
  }

  /** Solo propaga valores completos (hora, minutos y AM/PM) o el campo vacío, igual que el input nativo. */
  private emitIfChanged(): void {
    let value: string;
    if (this.hour.length === 2 && this.minute.length === 2 && this.period) {
      const h12 = Number(this.hour);
      const h24 = this.period === 'AM' ? (h12 % 12) : (h12 % 12) + 12;
      value = `${this.pad(h24)}:${this.minute}`;
    } else if (!this.hour && !this.minute) {
      this.period = null;
      value = '';
    } else {
      return;
    }
    if (value === this.lastValue) { return; }
    this.lastValue = value;
    this.onChange(value);
    this.timeChange.emit(value);
  }

  private pad(value: number): string {
    return pad2(value);
  }
}
